import React, { useState, useRef, useCallback, useEffect } from 'react';
import { isSoundEnabled, setSoundEnabled, playNotification } from '../services/notificationSound';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import './SettingsPage.css';

const API_URL = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5000/api/crm';

const SettingsPage = () => {
  const { profile } = useAuth();
  const userName = profile?.name || 'Usuário';
  const isAdmin = profile?.is_admin === true;
  const [soundOn, setSoundOn] = useState(isSoundEnabled);

  // Estados do fluxo
  const [step, setStep] = useState('upload'); // upload | preview | importing | done
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [analyzing, setAnalyzing] = useState(false);

  // Preview editável
  const [preview, setPreview] = useState(null);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');

  // Resultado
  const [result, setResult] = useState(null);

  const fileInputRef = useRef(null);

  // ── WhatsApp Connection ──────────────────────────────────────────────────
  const [waStatus, setWaStatus] = useState(null);   // null | 'connected' | 'disconnected'
  const [waInstance, setWaInstance] = useState('');
  const [waQrcode, setWaQrcode] = useState(null);
  const [waLoading, setWaLoading] = useState(false);
  const [waQrTimer, setWaQrTimer] = useState(60);
  const qrTimerRef = useRef(null);
  const statusPollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/whatsapp/status`);
      const data = await resp.json();
      setWaStatus(data.connected ? 'connected' : 'disconnected');
      setWaInstance(data.instance || '');
      if (data.connected) {
        setWaQrcode(null);
        clearInterval(qrTimerRef.current);
      }
    } catch {
      setWaStatus('disconnected');
    }
  }, []);

  const fetchQrcode = useCallback(async () => {
    setWaLoading(true);
    try {
      const resp = await fetch(`${API_URL}/whatsapp/qrcode`);
      const data = await resp.json();
      if (data.status === 'connected') {
        setWaStatus('connected');
        setWaQrcode(null);
        clearInterval(qrTimerRef.current);
      } else if (data.qrcode) {
        setWaQrcode(data.qrcode);
        setWaQrTimer(60);
      }
    } catch {
      // silencia — tenta de novo no próximo ciclo
    }
    setWaLoading(false);
  }, []);

  // Carrega status ao montar (só para admin)
  useEffect(() => {
    if (!isAdmin) return;
    fetchStatus();
  }, [isAdmin, fetchStatus]);

  // Quando desconectado: busca QR e inicia polling de status
  useEffect(() => {
    if (!isAdmin || waStatus !== 'disconnected') return;

    fetchQrcode();

    // Polling de status a cada 5s para detectar reconexão
    statusPollRef.current = setInterval(fetchStatus, 5000);

    // Timer do QR — renova a cada 55s (antes de expirar)
    qrTimerRef.current = setInterval(() => {
      setWaQrTimer(t => {
        if (t <= 1) {
          fetchQrcode();
          return 60;
        }
        return t - 1;
      });
    }, 1000);

    return () => {
      clearInterval(statusPollRef.current);
      clearInterval(qrTimerRef.current);
    };
  }, [isAdmin, waStatus, fetchStatus, fetchQrcode]);

  // ── Import handlers ──────────────────────────────────────────────────────
  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setError('Selecione um arquivo .zip exportado do WhatsApp');
      return;
    }

    setError('');
    setAnalyzing(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const resp = await fetch(`${API_URL}/import/analyze`, {
        method: 'POST',
        body: formData,
      });
      const data = await resp.json();

      if (!resp.ok) {
        setError(data.error || 'Erro ao analisar arquivo');
        setAnalyzing(false);
        return;
      }

      setPreview(data);
      setPhone(data.phone || '');
      setName(data.name || '');
      setStep('preview');
    } catch (e) {
      setError('Erro de conexão com o servidor');
    }
    setAnalyzing(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleFileSelect = useCallback((e) => {
    handleFile(e.target.files[0]);
  }, [handleFile]);

  const handleImport = async () => {
    if (!phone.trim()) {
      setError('Telefone é obrigatório');
      return;
    }
    if (!name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    setError('');
    setStep('importing');

    try {
      const resp = await fetch(`${API_URL}/import/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_id: preview.file_id,
          phone: phone.trim(),
          name: name.trim(),
        }),
      });
      const data = await resp.json();

      if (!resp.ok) {
        setError(data.error || 'Erro na importação');
        setStep('preview');
        return;
      }

      setResult(data);
      setStep('done');
    } catch (e) {
      setError('Erro de conexão');
      setStep('preview');
    }
  };

  const handleReset = () => {
    setStep('upload');
    setPreview(null);
    setPhone('');
    setName('');
    setResult(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatPhone = (val) => {
    const digits = val.replace(/\D/g, '');
    if (digits.length <= 2) return `+${digits}`;
    if (digits.length <= 4) return `+${digits.slice(0, 2)} ${digits.slice(2)}`;
    if (digits.length <= 9) return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;
    return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 9)}-${digits.slice(9, 13)}`;
  };

  return (
    <div style={{ display: 'flex', height: '100vh', flexDirection: 'column' }}>
      <Header subtitle="Configurações" userName={userName} />
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Sidebar activePage="config" />
        <div className="settings-container">
          <div className="settings-content">
            <h1 className="settings-title">Configurações</h1>

            {/* Card Notificações Sonoras */}
            <div className="settings-card">
              <div className="card-header">
                <div className="card-icon">🔔</div>
                <div>
                  <h2 className="card-title">Notificações sonoras</h2>
                  <p className="card-subtitle">
                    Som ao receber novas mensagens de clientes
                  </p>
                </div>
              </div>
              <div className="card-body">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text, #0f172a)' }}>
                      Som de notificação
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      Toca quando chega mensagem de outro cliente
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      onClick={() => playNotification()}
                      style={{
                        background: 'none', border: '0.5px solid #e2e8f0',
                        borderRadius: 6, padding: '4px 10px', fontSize: 12,
                        color: '#64748b', cursor: 'pointer'
                      }}
                    >
                      ▶ Testar
                    </button>
                    <label style={{ position: 'relative', display: 'inline-block', width: 36, height: 20, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={soundOn}
                        onChange={(e) => {
                          setSoundOn(e.target.checked);
                          setSoundEnabled(e.target.checked);
                        }}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: soundOn ? '#09077D' : '#e2e8f0',
                        borderRadius: 10, transition: 'background 0.2s'
                      }} />
                      <span style={{
                        position: 'absolute', top: 2, left: soundOn ? 18 : 2,
                        width: 16, height: 16, background: 'white',
                        borderRadius: '50%', transition: 'left 0.2s'
                      }} />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Card Importar WhatsApp */}
            <div className="settings-card" style={{ marginTop: 20 }}>
              <div className="card-header">
                <div className="card-icon">💬</div>
                <div>
                  <h2 className="card-title">Importar Conversa WhatsApp</h2>
                  <p className="card-subtitle">
                    Faça upload do arquivo .zip exportado do WhatsApp para importar o histórico
                  </p>
                </div>
              </div>

              <div className="card-body">
                {/* Erro */}
                {error && (
                  <div className="error-banner">
                    <span>⚠️</span> {error}
                  </div>
                )}

                {/* Step 1: Upload */}
                {step === 'upload' && (
                  <div
                    className={`drop-zone ${dragOver ? 'drop-zone-active' : ''} ${analyzing ? 'drop-zone-analyzing' : ''}`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => !analyzing && fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".zip"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    {analyzing ? (
                      <>
                        <div className="spinner" />
                        <p className="drop-text">Analisando conversa...</p>
                      </>
                    ) : (
                      <>
                        <div className="drop-icon">📁</div>
                        <p className="drop-text">
                          Arraste o arquivo .zip aqui ou <span className="drop-link">selecione</span>
                        </p>
                        <p className="drop-hint">
                          WhatsApp → Conversa → ⋮ → Exportar conversa → Sem mídia
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* Step 2: Preview editável */}
                {step === 'preview' && preview && (
                  <div className="preview-container">
                    <div className="stats-row">
                      <div className="stat-box">
                        <span className="stat-value">{preview.message_count}</span>
                        <span className="stat-label">Mensagens</span>
                      </div>
                      <div className="stat-box">
                        <span className="stat-value">{preview.inbound}</span>
                        <span className="stat-label">← Recebidas</span>
                      </div>
                      <div className="stat-box">
                        <span className="stat-value">{preview.outbound}</span>
                        <span className="stat-label">→ Enviadas</span>
                      </div>
                      <div className="stat-box">
                        <span className="stat-value">{preview.media}</span>
                        <span className="stat-label">📎 Mídia</span>
                      </div>
                    </div>

                    {preview.date_from && (
                      <p className="date-range">
                        📅 {preview.date_from} a {preview.date_to}
                      </p>
                    )}

                    {preview.contact_exists && (
                      <div className="info-banner">
                        ✅ Contato já existe no CRM
                      </div>
                    )}

                    <div className="form-group">
                      <label className="form-label">
                        Nome do contato
                        {!preview.name && <span className="required-dot">*</span>}
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ex: Maria Silva"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Telefone
                        {!preview.phone && <span className="required-dot">*</span>}
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/[^\d+\s\-()]/g, ''))}
                        placeholder="Ex: 5577999001234"
                      />
                      {phone && (
                        <span className="phone-formatted">{formatPhone(phone.replace(/\D/g, ''))}</span>
                      )}
                    </div>

                    <div className="actions-row">
                      <button className="btn-secondary" onClick={handleReset}>
                        Cancelar
                      </button>
                      <button
                        className="btn-primary"
                        onClick={handleImport}
                        disabled={!phone.trim() || !name.trim()}
                      >
                        Importar {preview.message_count} mensagens
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Importando */}
                {step === 'importing' && (
                  <div className="importing-container">
                    <div className="spinner large" />
                    <p className="importing-text">Importando mensagens...</p>
                    <p className="importing-hint">Isso pode levar alguns segundos</p>
                  </div>
                )}

                {/* Step 4: Concluído */}
                {step === 'done' && result && (
                  <div className="done-container">
                    <div className="done-icon">✅</div>
                    <h3 className="done-title">Importação concluída!</h3>

                    <div className="done-stats">
                      <p><strong>{result.contact_name}</strong></p>
                      <p>{result.inserted} mensagens inseridas</p>
                      {result.skipped > 0 && (
                        <p className="done-skipped">{result.skipped} duplicatas ignoradas</p>
                      )}
                      {result.is_new_contact && (
                        <p className="done-new">Novo contato criado no CRM</p>
                      )}
                    </div>

                    <div className="actions-row">
                      <button className="btn-primary" onClick={handleReset}>
                        Importar outra conversa
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Card Conexão WhatsApp — visível só para admin */}
            {isAdmin && (
              <div className="settings-card" style={{ marginTop: 20 }}>
                <div className="card-header">
                  <div className="card-icon" style={{ background: '#e8f0f9' }}>
                    📱
                  </div>
                  <div>
                    <h2 className="card-title">Conexão WhatsApp</h2>
                    <p className="card-subtitle">
                      Status da instância e reconexão por QR code
                    </p>
                  </div>
                </div>

                <div className="card-body">

                  {/* Carregando */}
                  {waStatus === null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#6b7280', fontSize: 14 }}>
                      <div className="spinner" style={{ margin: 0 }} />
                      Verificando conexão...
                    </div>
                  )}

                  {/* Conectado */}
                  {waStatus === 'connected' && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: '#10b981', flexShrink: 0
                        }} />
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>Conectado</div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                            {waInstance}
                          </div>
                        </div>
                      </div>
                      <button
                        className="btn-secondary"
                        onClick={fetchStatus}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                      >
                        ↻ Verificar
                      </button>
                    </div>
                  )}

                  {/* Desconectado */}
                  {waStatus === 'disconnected' && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{
                            width: 10, height: 10, borderRadius: '50%',
                            background: '#ef4444', flexShrink: 0,
                            animation: 'waPulse 1.5s infinite'
                          }} />
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>Desconectado</div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                              Escaneie o QR code para reconectar
                            </div>
                          </div>
                        </div>
                        <button
                          className="btn-secondary"
                          onClick={fetchQrcode}
                          disabled={waLoading}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                        >
                          ↻ Novo QR
                        </button>
                      </div>

                      {waLoading && (
                        <div style={{ textAlign: 'center', padding: '24px 0' }}>
                          <div className="spinner" />
                          <p style={{ fontSize: 13, color: '#6b7280', margin: '8px 0 0' }}>Gerando QR code...</p>
                        </div>
                      )}

                      {!waLoading && waQrcode && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                          <div style={{
                            width: 192, height: 192, padding: 8,
                            background: 'white', border: '2px solid #e5e7eb',
                            borderRadius: 12, display: 'flex',
                            alignItems: 'center', justifyContent: 'center'
                          }}>
                            <img
                              src={waQrcode.startsWith('data:') ? waQrcode : `data:image/png;base64,${waQrcode}`}
                              alt="QR Code WhatsApp"
                              style={{ width: 176, height: 176 }}
                            />
                          </div>

                          <div style={{ width: 192, height: 3, background: '#e5e7eb', borderRadius: 2 }}>
                            <div style={{
                              height: '100%', borderRadius: 2,
                              background: '#003A7A',
                              width: `${(waQrTimer / 60) * 100}%`,
                              transition: 'width 1s linear'
                            }} />
                          </div>

                          <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
                            Expira em <span style={{ color: '#ef4444', fontWeight: 600 }}>{waQrTimer}s</span> — novo QR gerado automaticamente
                          </p>
                          <p style={{ fontSize: 13, color: '#6b7280', margin: 0, textAlign: 'center' }}>
                            WhatsApp → Dispositivos conectados → Conectar dispositivo
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      <style>{`
        @keyframes waPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

export default SettingsPage;
